import { ref } from 'vue';
import type { AudioRecorderType } from '../audio-recorder';

type Step = 'COUNTDOWN_TO_START' | 'RECORDING' | 'RECORD_SUCCESS';

const DEFAULT_SECONDS_TO_START = 2;
const DEFAULT_SECONDS_TO_STOP = 10;
const DEFAULT_SECONDS_TO_SET_UP_STOP_COUNTDOWN = 50;

export function useAudioRecorderState(options: {
  audioRecorder: AudioRecorderType;
  onRecordingSuccess: (data: Blob, duration: number) => Promise<void> | void;
  onError: (error: Error) => void;
}) {
  const step = ref<Step>('COUNTDOWN_TO_START');
  const isGrantingPermission = ref(false);
  const secondsToStart = ref(DEFAULT_SECONDS_TO_START);
  const secondsToStop = ref(DEFAULT_SECONDS_TO_STOP);
  const showStopCountdown = ref(false);
  
  // 使用一个对象管理所有定时器
  const timers = {
    start: null as NodeJS.Timeout | null,
    stop: null as NodeJS.Timeout | null,
    setupStop: null as NodeJS.Timeout | null,
  };

  // 清理所有定时器
  function clearAllTimers() {
    if (timers.start) {
      clearInterval(timers.start);
      timers.start = null;
    }
    if (timers.stop) {
      clearInterval(timers.stop);
      timers.stop = null;
    }
    if (timers.setupStop) {
      clearTimeout(timers.setupStop);
      timers.setupStop = null;
    }
  }

  // 重置状态
  function resetState() {
    isGrantingPermission.value = false;
    step.value = 'COUNTDOWN_TO_START';
    secondsToStart.value = DEFAULT_SECONDS_TO_START;
    secondsToStop.value = DEFAULT_SECONDS_TO_STOP;
  }

  // 开始倒计时
  function startCountdownBeforeStart() {
    clearAllTimers();
    resetState();
    
    timers.start = setInterval(() => {
      secondsToStart.value--;
      if (secondsToStart.value === 0) {
        clearAllTimers();
        handleStart();
      }
    }, 1000);
  }

  // 开始录音
  async function handleStart() {
    isGrantingPermission.value = true;
    try {
      await options.audioRecorder.start();
      step.value = 'RECORDING';
      // 设置停止倒计时定时器
      timers.setupStop = setTimeout(() => {
        countDownToStop();
      }, DEFAULT_SECONDS_TO_SET_UP_STOP_COUNTDOWN * 1000);
    } catch (error) {
      console.error('开始录音失败:', error);
      options.onError(error as Error);
    } finally {
      isGrantingPermission.value = false;
    }
  }

  // 停止倒计时
  function countDownToStop() {
    // 确保当前状态是正在录音
    if (step.value !== 'RECORDING') return;
    showStopCountdown.value = true;
    clearAllTimers();
    secondsToStop.value = DEFAULT_SECONDS_TO_STOP;
    
    timers.stop = setInterval(() => {
      secondsToStop.value--;
      // 确保不会小于0
      if (secondsToStop.value <= 0) {
        secondsToStop.value = 0;
        clearAllTimers();
        handleStop();
      }
    }, 1000);
  }

  // 停止录音
  async function handleStop() {
    try {
      step.value = 'RECORD_SUCCESS';
      showStopCountdown.value = false;
      const { data, duration } = await options.audioRecorder.stop();
      if (!data) {
        throw new Error('录音数据为空');
      }
      
      await options.onRecordingSuccess(data, duration);
    } catch (error) {
      options.onError(error as Error);
      console.error('停止录音失败:', error);
    }
  }

  // 清理状态
  function clean() {
    clearAllTimers();
    resetState();
  }

  return {
    step,
    secondsToStart,
    secondsToStop,
    startCountdownBeforeStart,
    handleStart,
    handleStop,
    clean,
    showStopCountdown,
    isGrantingPermission
  };
}